import React from "react";
import PropTypes from "prop-types";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Column,
  Fieldset,
  Field,
  Control,
  Input,
  Label,
  Title,
  Box,
  Button,
  Icon
} from "rbx";

import {
  TripIntervalSelect,
  LocationSelect,
  PoorTextEditor,
  BooleanInput,
  SystemCodeSelect
} from "../../../../components/inputs";

import { ourToast as toast } from "../../../../utils";

import { CalcTypeUsage } from "../components";

const Settings = ({ editing, inputs, onChange }) => {
  const [showPassword, setShowPassword] = React.useState(false);

  const toggleShowPassword = e => {
    e.preventDefault();
    e.stopPropagation();
    setShowPassword(!showPassword);
  };

  const handleChange = async e => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    const { name } = e.target;
    if (name === "UsePrimaryStorageDimension") {
      if (!value) {
        onChange(name, value);
        onChange("UseSecondaryStorageDimension", false);
        onChange("UseTertiaryStorageDimension", false);
      } else {
        onChange(name, value);
      }
    } else if (name === "UseSecondaryStorageDimension") {
      if (!value) {
        onChange(name, value);
        onChange("UseTertiaryStorageDimension", false);
      } else if (inputs.UsePrimaryStorageDimension && value) {
        onChange(name, value);
      } else {
        toast.success("Select Primary Storage first.");
      }
    } else if (name === "UseTertiaryStorageDimension") {
      if (inputs.UseSecondaryStorageDimension) {
        onChange(name, value);
      } else {
        toast.success("Select Secondary Storage first.");
      }
    } else if (name === "PrimaryStorageDimension") {
      onChange("PrimaryStorageDimension", {
        ...inputs.PrimaryStorageDimension,
        CodeDescription: value
      });
    } else if (name === "SecondaryStorageDimension") {
      onChange("SecondaryStorageDimension", {
        ...inputs.SecondaryStorageDimension,
        CodeDescription: value
      });
    } else if (name === "TertiaryStorageDimension") {
      onChange("TertiaryStorageDimension", {
        ...inputs.TertiaryStorageDimension,
        CodeDescription: value
      });
    } else {
      onChange(name, value);
    }
  };
  return (
    <div className="company-settings-page">
      <Fieldset disabled={!editing}>
        <Column.Group>
          <Column size={6}>
            <Box style={{ height: "100%" }}>
              <Title subtitle>Dispatching</Title>
              <Column.Group>
                <Column size={4}>
                  <Field>
                    <Label>Next Order Number</Label>
                    <Control>
                      <Input
                        autoComplete="new"
                        defaultValue="0"
                        name="NextOrderNo"
                        type="text"
                        value={inputs.NextOrderNo}
                        onChange={handleChange}
                      />
                    </Control>
                  </Field>
                </Column>
                <Column size={4}>
                  <TripIntervalSelect
                    label="Default Trip Interval"
                    name="DefTripInterval"
                    value={inputs.DefTripIntervalRecIdRef}
                    onChange={handleChange}
                  />
                </Column>
              </Column.Group>
              <Column.Group>
                <Column size={4}>
                  <Field>
                    <Label>Next Work Order Number</Label>
                    <Control>
                      <Input
                        autoComplete="new"
                        defaultValue="0"
                        name="NextWONo"
                        type="text"
                        value={inputs.NextWONo}
                        onChange={handleChange}
                      />
                    </Control>
                  </Field>
                </Column>
                <Column size={4}>
                  <LocationSelect
                    displayFn={Location =>
                      `${Location.LocationID} - ${Location.LocationName}`
                    }
                    label="Default Pickup Location"
                    name="DefPickupLocationRecIdRef"
                    showHelp={false}
                    value={inputs.DefPickupLocationRecIdRef}
                    where={{ PickupLocation: true }}
                    onChange={handleChange}
                  />
                </Column>
              </Column.Group>
              <Column.Group>
                <Column size={4}>
                  <Field>
                    <Label>Next Invoice Number</Label>
                    <Control>
                      <Input
                        autoComplete="new"
                        defaultValue="0"
                        name="NextInvcNo"
                        type="text"
                        value={inputs.NextInvcNo}
                        onChange={handleChange}
                      />
                    </Control>
                  </Field>
                </Column>
                <Column size={4}>
                  <Field>
                    <Label>Labor Hour Increment</Label>
                    <Control>
                      <Input
                        autoComplete="new"
                        min=".01"
                        name="LaborHourIncrement"
                        step=".01"
                        type="number"
                        value={inputs.LaborHourIncrement}
                        onChange={handleChange}
                      />
                    </Control>
                  </Field>
                </Column>
              </Column.Group>
            </Box>
          </Column>
          <Column size={6}>
            <Box style={{ height: "100%" }}>
              <Title subtitle>Inventory</Title>
              <Column.Group>
                <Column size={4}>
                  <Field>
                    <Control>
                      <Label>Use Primary Storage Dimension</Label>
                      <BooleanInput
                        name="UsePrimaryStorageDimension"
                        value={inputs.UsePrimaryStorageDimension}
                        onChange={handleChange}
                      />
                    </Control>
                  </Field>
                </Column>
                <Column size={4}>
                  <Field>
                    <Control>
                      <Label>Primary Label</Label>
                      <Input
                        autoComplete="new"
                        disabled={!inputs.UsePrimaryStorageDimension}
                        name="PrimaryStorageDimension"
                        type="text"
                        value={
                          inputs.PrimaryStorageDimension &&
                          inputs.PrimaryStorageDimension.CodeDescription
                        }
                        onChange={handleChange}
                      />
                    </Control>
                  </Field>
                </Column>
                <Column size={4}>
                  <SystemCodeSelect
                    icon="globe"
                    label="Costing Method"
                    name="InvCostMethRecIdRef"
                    value={inputs.InvCostMethRecIdRef}
                    where={{ Category: "Inventory", CodeName: "INVCOSTMETH" }}
                    onChange={handleChange}
                  />
                </Column>
              </Column.Group>
              <Column.Group>
                <Column size={4}>
                  <Field>
                    <Control>
                      <Label>Use Secondary Storage Dimension</Label>
                      <BooleanInput
                        name="UseSecondaryStorageDimension"
                        value={inputs.UseSecondaryStorageDimension}
                        onChange={handleChange}
                      />
                    </Control>
                  </Field>
                </Column>
                <Column size={4}>
                  <Field>
                    <Control>
                      <Label>Secondary Label Storage Dimension</Label>
                      <Input
                        autoComplete="new"
                        disabled={!inputs.UseSecondaryStorageDimension}
                        name="SecondaryStorageDimension"
                        type="text"
                        value={
                          inputs.SecondaryStorageDimension &&
                          inputs.SecondaryStorageDimension.CodeDescription
                        }
                        onChange={handleChange}
                      />
                    </Control>
                  </Field>
                </Column>
              </Column.Group>
              <Column.Group>
                <Column size={4}>
                  <Field>
                    <Control>
                      <Label>Use Tertiary Storage Dimension</Label>
                      <BooleanInput
                        name="UseTertiaryStorageDimension"
                        value={inputs.UseTertiaryStorageDimension}
                        onChange={handleChange}
                      />
                    </Control>
                  </Field>
                </Column>
                <Column size={4}>
                  <Field>
                    <Control>
                      <Label>Tertiary Label</Label>
                      <Input
                        autoComplete="new"
                        disabled={!inputs.UseTertiaryStorageDimension}
                        name="TertiaryStorageDimension"
                        type="text"
                        value={
                          inputs.TertiaryStorageDimension &&
                          inputs.TertiaryStorageDimension.CodeDescription
                        }
                        onChange={handleChange}
                      />
                    </Control>
                  </Field>
                </Column>
              </Column.Group>
            </Box>
          </Column>
        </Column.Group>
      </Fieldset>
      <Fieldset disabled={!editing}>
        <Column.Group style={{ marginTop: "1rem" }}>
          <Column size={6}>
            <Box style={{ height: "100%" }}>
              <Title subtitle style={{ marginTop: "1rem" }}>
                Finance
              </Title>
              <CalcTypeUsage inputs={inputs} onChange={handleChange} />
              <Field kind="group">
                <Control expanded>
                  <Label>Detention Max. Load Time (hrs)</Label>
                  <Input
                    disabled={!editing}
                    name="MaxLoadTime"
                    type="number"
                    value={inputs.MaxLoadTime}
                    onChange={handleChange}
                  />
                </Control>
                <Control expanded>
                  <Label>Detention Max. Unload Time (hrs)</Label>
                  <Input
                    disabled={!editing}
                    name="MaxUnloadTime"
                    type="number"
                    value={inputs.MaxUnloadTime}
                    onChange={handleChange}
                  />
                </Control>
              </Field>
              <Control expanded>
                <Label className="UpdatePasswordModalLabel">Password</Label>
                <Field kind="addons">
                  <Control expanded>
                    <Input
                      required
                      className="FiscalPeriodPassword"
                      maxLength={250}
                      name="Password"
                      placeholder="Fiscal Period Password"
                      type={showPassword ? "text" : "password"}
                      value={inputs.FiscalPeriodPassword}
                      onChange={handleChange}
                    />
                  </Control>
                  <Control>
                    <Button
                      className="input UpdatePasswordModalToggleShowButton"
                      color="info"
                      tabIndex="-1"
                      onClick={toggleShowPassword}
                    >
                      <Icon>
                        <FontAwesomeIcon
                          icon={showPassword ? "eye" : "eye-slash"}
                        />
                      </Icon>
                    </Button>
                  </Control>
                </Field>
              </Control>
            </Box>
          </Column>
          <Column size={6}>
            <Box style={{ height: "100%" }}>
              <Title subtitle style={{ marginTop: "1rem" }}>
                Invoice Note
              </Title>
              <PoorTextEditor
                name="InvoiceNote"
                value={inputs.InvoiceNote}
                onChange={handleChange}
              />
            </Box>
          </Column>
        </Column.Group>
      </Fieldset>
    </div>
  );
};

Settings.propTypes = {
  editing: PropTypes.object,
  inputs: PropTypes.object,
  onChange: PropTypes.func
};
export default Settings;
